/**
 * Prop Studio — the viewer: the procedural prop and the candidate model, side
 * by side or one over the other, on a floor grid in FEET, against the wall
 * the prop stands on.
 *
 * Plain three.js, no render pipeline: the point is to see whether the model
 * sits in the kit's box the way the procedural prop does, not to light it
 * well. The candidate is placed by placement.ts — the same arithmetic as the
 * readouts and (in Python) the renderer — so what is seen here is what the
 * render gets; it is placed the moment it loads, never drawn raw. Drawn on
 * demand: a frame when the camera moves or something changes, never a loop.
 *
 * NEVER imported by a jest test: three's example modules (OrbitControls,
 * GLTFLoader) are ESM that jest does not transform. The pure logic lives in
 * placement.ts for that reason.
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { PropDef } from '../../calculations/staging/kits';
import { buildFloorProp, buildHungProp } from '../ui/room3d/propBuilder';
import { loadAccessoryGeometry } from '../ui/room3d/accessoryMeshes';
import { accessoryDef } from '../../data/slatwallAccessories';
import { Bounds, Mat3, PropTransform, ThreePlacement, Vec3, blenderToGltf, copyOffsetsFeet, gltfToBlender, placeInThree, placedBoundsFeet, stagedSizeFeet } from './placement';
import { PropFrame, heldAs, heldOffset, stagedFrame } from './propFrame';
import { PROP_STUDIO_STRINGS as S } from './strings';

export type ViewerMode = 'side' | 'overlay';

/** What has become of the candidate's .glb, for the panels to say. */
export type CandidateState =
  | { phase: 'none' }
  | { phase: 'loading' }
  | { phase: 'ready'; sizeMetres: Vec3 }
  | { phase: 'error'; message: string };

/** The candidate as it is DRAWN: the loaded object's own transform, and what is visible. */
export interface CandidatePlacement extends ThreePlacement { visible: boolean; twinVisible: boolean }

/** What the page (and through it the headless driver) can read off the viewer. */
export interface ViewerHandle {
  /** The camera on the model alone (its placed box filling the view), and back on the whole arrangement. */
  focusModel: () => void;
  focusAll: () => void;
  /**
   * The lowest point of the model's actual geometry as placed now (feet, the
   * prop's frame: 0 is the floor) — the mesh, not its box, so a rolled bike
   * reads by its wheels. Null while no model is drawn.
   */
  modelLowestY: () => number | null;
  /** The model's actual geometry as placed now: its lowest point and its nearest point to the wall (feet, the prop's frame). Null while no model is drawn. */
  modelExtremes: () => { minY: number; minZ: number } | null;
  /** The candidate object's actual position, turn and scale (feet, three.js axes); null when nothing is loaded. */
  candidatePlacement: () => CandidatePlacement | null;
  /** A part's object as placed in the candidate's frame; null while its preview is not loaded. */
  partPlacement: (key: string) => CandidatePlacement | null;
  /** Where a point of the candidate model (its own frame, Blender axes, metres) is on screen (client px); null when not drawn. */
  screenOfModelPoint: (p: Vec3) => { x: number; y: number } | null;
  /** The candidate's vertex nearest the camera (its own frame, Blender axes): a spot on its surface nothing hides. */
  frontModelVertex: () => Vec3 | null;
}

/** A part of the candidate (a toolbox on the bench): its preview, bounds and placement in the candidate's frame. */
export interface ViewerPart {
  key: string;
  url: string | null;
  modelBounds: Bounds | null;
  transform: PropTransform;
  /** The part being placed now: drawn with a box round it. */
  selected: boolean;
}

export interface PropViewerProps {
  def: PropDef;
  /** The candidate's preview .glb (null: nothing to show) and the bounds recorded with it. */
  candidateUrl: string | null;
  modelBounds: Bounds | null;
  transform: PropTransform;
  /** The candidate's parts, drawn with it in its frame. */
  parts?: ViewerPart[];
  mode: ViewerMode;
  /** Arrow keys with the viewer focused: feet along the wall and into the room. */
  onNudge?: (dx: number, dz: number) => void;
  onCandidate?: (state: CandidateState) => void;
  /** Show the hang point: a held prop's model gets a marker where its hang point lands (README "Held props: the hang point"). */
  showHang?: boolean;
  /** The hold and hang markers (the gold ball, the blue ring) drawn at all; off to place a model unobstructed. Default on. */
  showMarkers?: boolean;
  /** Picking the hang point: the next click (not a drag) on the model sets it; Esc cancels. */
  picking?: boolean;
  /** The spot clicked on the model, in its own frame (Blender axes, metres) — what `hang` stores. */
  onPick?: (hang: Vec3) => void;
  onCancelPick?: () => void;
}

/** The designer's accent: the kit box. */
const ACCENT = 0xfeb100;
/** The hang point on the model: not the kit's gold, so the two read apart when they are not together. */
const HANG = 0x4fd6ff;
/** The "room" arrow: the green the designer uses for a live status. */
const ARROW = 0x9fe0b0;
/** The wall: pale, so the model shows through where it passes behind. */
const WALL = 0xdfe3ea;
const BACKGROUND = 0x1f2126;
/** Between the procedural prop's box and the candidate's, side by side (feet). */
const GAP_FT = 2;
/** The wall runs this much past the boxes either side (feet). */
const WALL_MARGIN_FT = 2;
const GHOST_OPACITY = 0.35;

interface Rig {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  /** The floor grid: at the origin's height, or a wall prop's "Held at" height below the hold point. */
  grid: THREE.GridHelper;
  /** The procedural prop and its kit box, at the origin. */
  procedural: THREE.Group | null;
  kitBox: THREE.LineSegments | null;
  /** What the prop keeps clear (makeClearZone), or null when nothing. */
  clearZone: THREE.Group | null;
  /** The model's PLACED box (what staging keeps room for once it has a model), round the candidate. */
  placedBox: THREE.LineSegments | null;
  /** The Trusscore accessory the prop is previewed on (its STL, on the wall, holding at the origin), and its twin beside the model. */
  accessory: THREE.Group | null;
  accessoryTwin: THREE.Group | null;
  /** A held prop's hold point, at the origin. */
  holdMarker: THREE.Mesh | null;
  /** The candidate's hang point, where it lands (in the candidate's frame); drawn through the model. */
  hangMarker: THREE.Mesh;
  /** The hold point again, beside the candidate (side by side): the hook its hang point goes on. */
  holdTwin: THREE.Mesh;
  /** The kit box again, round the candidate when it stands beside the prop. */
  kitBoxTwin: THREE.LineSegments | null;
  arrow: THREE.Group | null;
  /** The wall line z = −d/2: a translucent plane and a strong line along the floor, unit-sized and stretched to fit. */
  wall: THREE.Mesh;
  wallLine: THREE.Mesh;
  /** A corner prop's second wall, along its left side (staging puts the corner there); hidden for every other prop. */
  sideWall: THREE.Mesh;
  sideWallLine: THREE.Mesh;
  /** Holds the candidate; moved beside the prop in side-by-side mode. */
  candidateRoot: THREE.Group;
  candidate: THREE.Object3D | null;
  /** The candidate's further copies (README step 6): clones that SHARE its geometry and materials. */
  copies: THREE.Object3D[];
  /** The candidate's parts, by key: each loaded from its own preview, with its copies. */
  parts: Map<string, { url: string | null; root: THREE.Object3D | null; copies: THREE.Object3D[]; token: number }>;
  /** A box round the part being placed. */
  partBox: THREE.LineSegments | null;
  /** The parts' load counter (the candidate's is loadToken). */
  partLoads: number;
  /** Which load is current: a slow load finishing after a newer one is ignored. */
  loadToken: number;
  frames: number;
  render: () => void;
}

function disposeObject(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    for (const m of mats) {
      // Every texture the material holds (map, normalMap, …) goes with it.
      for (const value of Object.values(m)) {
        if (value && (value as THREE.Texture).isTexture) (value as THREE.Texture).dispose();
      }
      m.dispose();
    }
  });
}

/** A wire box of the kit's box in the prop's frame (a floor prop's stands on the floor; a held prop's hangs from the origin). */
function makeKitBox(box: Bounds, opacity: number, color: THREE.ColorRepresentation = ACCENT): THREE.LineSegments {
  const size = [box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2]];
  const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(size[0], size[1], size[2]));
  const material = new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity });
  const lines = new THREE.LineSegments(edges, material);
  lines.position.set((box.min[0] + box.max[0]) / 2, (box.min[1] + box.max[1]) / 2, (box.min[2] + box.max[2]) / 2);
  return lines;
}

/**
 * The prop's SAFE ZONE, as staging keeps it: for a floor prop a translucent
 * rectangle on the floor — the box plus its front clearance, its side
 * clearance either way, and the gap it keeps off the wall behind; for a held
 * prop a rectangle on the wall, the box widened by its side clearance. Null
 * when the prop keeps nothing clear.
 */
function makeClearZone(def: PropDef, frame: PropFrame): THREE.Group | null {
  const side = def.clearSide ?? 0;
  const front = frame.kind === 'floor' ? def.clearance ?? 0 : 0;
  const back = frame.kind === 'floor' ? def.clearBack ?? 0 : 0;
  if (side <= 0 && front <= 0 && back <= 0) return null;
  const b = frame.box;
  const g = new THREE.Group();
  const fill = new THREE.MeshBasicMaterial({ color: ACCENT, transparent: true, opacity: 0.1, depthWrite: false, side: THREE.DoubleSide });
  const edge = new THREE.LineBasicMaterial({ color: ACCENT, transparent: true, opacity: 0.5 });
  if (frame.kind === 'floor') {
    const w = b.max[0] - b.min[0] + 2 * side;
    const d = (b.max[2] + front) - frame.wallZ;
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(w, d), fill);
    plane.rotation.x = -Math.PI / 2;
    plane.position.set((b.min[0] + b.max[0]) / 2, 0.01, frame.wallZ + d / 2);
    const outline = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, d)), edge);
    outline.rotation.x = -Math.PI / 2;
    outline.position.copy(plane.position);
    g.add(plane, outline);
  } else {
    const w = b.max[0] - b.min[0] + 2 * side;
    const h = b.max[1] - b.min[1];
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), fill);
    plane.position.set((b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, frame.wallZ + 0.01);
    const outline = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, h)), edge);
    outline.position.copy(plane.position);
    g.add(plane, outline);
  }
  return g;
}

/** A held prop's hold point: a small ball at the origin, where the hook's prong (or the shelf's top) is. */
function makeHangMarker(): THREE.Mesh {
  // A ring round a dot, through everything: the spot on the model that goes on the hook.
  const ball = new THREE.Mesh(new THREE.SphereGeometry(0.06, 12, 8), new THREE.MeshBasicMaterial({ color: HANG, depthTest: false, transparent: true, opacity: 0.95 }));
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.018, 6, 24), new THREE.MeshBasicMaterial({ color: HANG, depthTest: false, transparent: true }));
  ring.renderOrder = 21;
  ball.add(ring);
  ball.renderOrder = 21;
  ball.visible = false;
  return ball;
}

/** The hold point (the hook). `through`: drawn over whatever is in front of it — beside a model that covers it. */
function makeHoldMarker(through = false): THREE.Mesh {
  const ball = new THREE.Mesh(new THREE.SphereGeometry(0.08, 12, 8), new THREE.MeshBasicMaterial({ color: ACCENT, depthTest: !through, transparent: through }));
  ball.renderOrder = through ? 20 : 11;
  return ball;
}

/** An arrow out of the box's front along +z, with "room" at its tip. */
function makeRoomArrow(box: Bounds): THREE.Group {
  const g = new THREE.Group();
  const length = 1.5;
  const size = { d: box.max[2] - box.min[2] };
  g.add(new THREE.ArrowHelper(new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, box.min[1] + 0.05, box.max[2] + 0.15), length, ARROW, 0.4, 0.22));
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.font = 'bold 72px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#9fe0b0';
    ctx.fillText(S.viewer.roomArrow, 128, 64);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const label = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false }));
  label.scale.set(1.4, 0.7, 1);
  label.position.set(0, box.min[1] + 0.35, box.max[2] + 0.15 + length + 0.5);
  g.add(label);
  return g;
}

/**
 * The wall the prop stands against. A unit plane facing the room (PlaneGeometry
 * lies in x–y, which IS the wall's plane at a fixed z) and a unit bar along
 * its foot; the placement stretches both to the scene. Translucent so a model
 * that passes behind the line is still seen — in the render it would be cut
 * off by the Wall&CeilingBoard.
 */
function makeWall(): { plane: THREE.Mesh; line: THREE.Mesh } {
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({ color: WALL, transparent: true, opacity: 0.14, side: THREE.DoubleSide, depthWrite: false }),
  );
  const line = new THREE.Mesh(new THREE.BoxGeometry(1, 0.05, 0.05), new THREE.MeshBasicMaterial({ color: WALL }));
  return { plane, line };
}

/**
 * The prop at the size the room stages it: a floor prop with a model takes
 * the model's staged size (variants.ts stagedSizeFeet) in place of the
 * kit's box -- which, for a prop made in Prop Studio, is a placeholder
 * (1 x 1 x 1) the model's own size replaces. The stand-in, the kit box, the
 * side-by-side spacing and the camera all go by it, so the two read at one scale.
 */
function sizedDef(def: PropDef, modelBounds: Bounds | null | undefined, transform: PropTransform): PropDef {
  if (!modelBounds) return def;
  if (def.kind === 'floor' || def.kind === 'yard') return { ...def, size: stagedSizeFeet(placedBoundsFeet(modelBounds, transform), transform) };
  // A held prop's stand-in is the model's box about the hold point (heldAs): it follows the model.
  if ((def.kind === 'slatwall' || def.kind === 'wall') && !transform.stage) return heldAs(def, placedBoundsFeet(modelBounds, transform));
  return def;
}

/** Where a held prop's stand-in is drawn in its frame (along, out), when a model says where its box is; else at the hold. */
function heldShift(def: PropDef, modelBounds: Bounds | null | undefined, transform: PropTransform): [number, number] {
  if (!modelBounds || !(def.kind === 'slatwall' || def.kind === 'wall') || transform.stage) return [0, 0];
  return heldOffset(placedBoundsFeet(modelBounds, transform));
}

/** A view that takes in the prop (and the candidate beside it, when there is one), from the room side, a little above. */
function fitCamera(rig: Rig, frame: PropFrame, mode: ViewerMode, hasCandidate: boolean): void {
  const two = mode === 'side' && hasCandidate;
  const { box } = frame;
  const w = box.max[0] - box.min[0], h = box.max[1] - box.min[1], d = box.max[2] - box.min[2];
  const span = two ? 2 * w + GAP_FT : w;
  const cx = two ? (w + GAP_FT) / 2 : 0;
  const cy = (box.min[1] + box.max[1]) / 2;
  const radius = Math.max(span, h, d) * 0.6 + 1;
  const dist = (radius / Math.tan(THREE.MathUtils.degToRad(rig.camera.fov / 2))) * 1.1;
  rig.controls.target.set(cx, cy, 0);
  rig.camera.position.set(cx + dist * 0.45, cy + dist * 0.4, dist * 0.8);
  rig.controls.update();
}

/** The camera on the MODEL alone: its placed box (copies in), where it stands in the view, filling the frame. */
function fitCameraToModel(rig: Rig, modelBounds: Bounds, transform: PropTransform): void {
  const b = placedBoundsFeet(modelBounds, transform);
  const w = b.max[0] - b.min[0], h = b.max[1] - b.min[1], d = b.max[2] - b.min[2];
  const cx = rig.candidateRoot.position.x + (b.min[0] + b.max[0]) / 2;
  const cy = (b.min[1] + b.max[1]) / 2;
  const cz = (b.min[2] + b.max[2]) / 2;
  const radius = Math.max(w, h, d) * 0.6 + 0.3;
  const dist = (radius / Math.tan(THREE.MathUtils.degToRad(rig.camera.fov / 2))) * 1.1;
  rig.controls.target.set(cx, cy, cz);
  rig.camera.position.set(cx + dist * 0.45, cy + dist * 0.35, cz + dist * 0.8);
  rig.controls.update();
  rig.render();
}

export const PropViewer = forwardRef<ViewerHandle, PropViewerProps>(function PropViewer({ def, candidateUrl, modelBounds, transform, parts = [], mode, onNudge, onCandidate, showHang = false, showMarkers = true, picking = false, onPick, onCancelPick }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rigRef = useRef<Rig | null>(null);
  const onNudgeRef = useRef(onNudge);
  onNudgeRef.current = onNudge;
  const onCandidateRef = useRef(onCandidate);
  onCandidateRef.current = onCandidate;
  // The latest props, for the imperative handle (made once) to read.
  const latestRef = useRef({ def, modelBounds, transform, mode, candidateUrl });
  latestRef.current = { def, modelBounds, transform, mode, candidateUrl };

  useImperativeHandle(ref, () => {
    const placementOf = (c: THREE.Object3D | null | undefined): CandidatePlacement | null => {
      const rig = rigRef.current;
      if (!rig || !c) return null;
      return {
        position: [c.position.x, c.position.y, c.position.z],
        rotation: new THREE.Matrix3().setFromMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(c.quaternion)).transpose().toArray() as Mat3,
        quaternion: [c.quaternion.x, c.quaternion.y, c.quaternion.z, c.quaternion.w],
        scale: [c.scale.x, c.scale.y, c.scale.z],
        visible: c.visible,
        twinVisible: !!rig.kitBoxTwin?.visible,
      };
    };
    return {
      // The handle is made once (no deps): these read the CURRENT props through a ref.
      focusModel: () => {
        const rig = rigRef.current;
        const { modelBounds: mb, transform: t } = latestRef.current;
        if (rig && mb && rig.candidate?.visible) fitCameraToModel(rig, mb, t);
      },
      focusAll: () => {
        const rig = rigRef.current;
        const { def: d, mode: m, candidateUrl: url, modelBounds: mb, transform: t } = latestRef.current;
        if (rig) { fitCamera(rig, stagedFrame(d, mb, t), m, !!url); rig.render(); }
      },
      modelLowestY: () => {
        const rig = rigRef.current;
        const model = rig?.candidate;
        if (!rig || !model || !model.visible) return null;
        rig.scene.updateMatrixWorld(true);
        // Precise: every vertex through its mesh's world matrix (the frame's y is world y).
        const box = new THREE.Box3().setFromObject(model, true);
        return Number.isFinite(box.min.y) ? box.min.y : null;
      },
      modelExtremes: () => {
        const rig = rigRef.current;
        const model = rig?.candidate;
        if (!rig || !model || !model.visible) return null;
        rig.scene.updateMatrixWorld(true);
        // Every vertex of every mesh through its world matrix, less the
        // candidate root's own offset (the side-by-side shift is along x only,
        // so y and z are the prop frame's).
        const box = new THREE.Box3();
        const v = new THREE.Vector3();
        model.traverse((o) => {
          const mesh = o as THREE.Mesh;
          if (!mesh.isMesh || !mesh.geometry) return;
          const p = mesh.geometry.getAttribute('position');
          if (!p) return;
          for (let i = 0; i < p.count; i++) box.expandByPoint(v.fromBufferAttribute(p, i).applyMatrix4(mesh.matrixWorld));
        });
        if (box.isEmpty()) return null;
        return { minY: box.min.y - rig.candidateRoot.position.y, minZ: box.min.z - rig.candidateRoot.position.z };
      },
      candidatePlacement: () => placementOf(rigRef.current?.candidate),
      partPlacement: (key: string) => placementOf(rigRef.current?.parts.get(key)?.root),
      screenOfModelPoint: (p: Vec3) => {
        const rig = rigRef.current;
        const model = rig?.candidate;
        if (!rig || !model || !model.visible) return null;
        rig.scene.updateMatrixWorld(true);
        const g = blenderToGltf(p);
        const v = model.localToWorld(new THREE.Vector3(g[0], g[1], g[2])).project(rig.camera);
        const rect = rig.renderer.domElement.getBoundingClientRect();
        return { x: rect.left + ((v.x + 1) / 2) * rect.width, y: rect.top + ((1 - v.y) / 2) * rect.height };
      },
      frontModelVertex: () => {
        const rig = rigRef.current;
        const model = rig?.candidate;
        if (!rig || !model) return null;
        rig.scene.updateMatrixWorld(true);
        let best: THREE.Vector3 | null = null;
        let bestDist = Infinity;
        const w = new THREE.Vector3();
        model.traverse((o) => {
          const mesh = o as THREE.Mesh;
          const pos = mesh.isMesh ? (mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined) : undefined;
          if (!pos) return;
          const stride = Math.max(1, Math.floor(pos.count / 2000));
          for (let i = 0; i < pos.count; i += stride) {
            w.fromBufferAttribute(pos, i);
            mesh.localToWorld(w);
            const dist = w.distanceTo(rig.camera.position);
            if (dist < bestDist) { bestDist = dist; best = w.clone(); }
          }
        });
        if (!best) return null;
        const local = model.worldToLocal(best as THREE.Vector3);
        return gltfToBlender([local.x, local.y, local.z]);
      },
    };
  }, []);

  // The rig: made once, disposed on unmount.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      onCandidateRef.current?.({ phase: 'error', message: S.viewer.noWebgl });
      return undefined;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = false;
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(BACKGROUND);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.05, 1000);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;
    controls.maxPolarAngle = Math.PI; // under the floor too: the underside of a shelf or a held prop is where the placing happens

    // Soft, even light: enough to tell the shapes apart, no shadows to judge by.
    scene.add(new THREE.HemisphereLight(0xffffff, 0x3b3f46, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 1.8);
    key.position.set(6, 10, 8);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(-8, 4, -6);
    scene.add(fill);

    // A 20 × 20 ft floor, 1 ft cells.
    const grid = new THREE.GridHelper(20, 20, 0x8a8f99, 0x3a3e46);
    (grid.material as THREE.Material).transparent = true;
    (grid.material as THREE.Material).opacity = 0.7;
    scene.add(grid);

    const candidateRoot = new THREE.Group();
    scene.add(candidateRoot);
    const { plane: wall, line: wallLine } = makeWall();
    scene.add(wall, wallLine);
    const { plane: sideWall, line: sideWallLine } = makeWall();
    sideWall.rotation.y = Math.PI / 2;
    sideWallLine.rotation.y = Math.PI / 2;
    sideWall.visible = sideWallLine.visible = false;
    scene.add(sideWall, sideWallLine);

    const rig: Rig = {
      renderer, scene, camera, controls, grid, procedural: null, kitBox: null, clearZone: null, placedBox: null, accessory: null, accessoryTwin: null, holdMarker: null, kitBoxTwin: null, arrow: null, wall, wallLine, sideWall, sideWallLine, candidateRoot, candidate: null, copies: [], parts: new Map(), partBox: null, partLoads: 0, hangMarker: makeHangMarker(), holdTwin: makeHoldMarker(true), loadToken: 0, frames: 0,
      render: () => {
        renderer.render(scene, camera);
        rig.frames += 1;
        // For tooling: how many frames this canvas has drawn.
        renderer.domElement.dataset.frames = String(rig.frames);
      },
    };
    rigRef.current = rig;

    const resize = () => {
      const w = container.clientWidth, h = container.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      rig.render();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    controls.addEventListener('change', rig.render);

    return () => {
      observer.disconnect();
      controls.removeEventListener('change', rig.render);
      controls.dispose();
      rig.loadToken += 1; // any load still in flight is ignored
      if (rig.candidate) disposeObject(rig.candidate);
      if (rig.procedural) disposeObject(rig.procedural);
      if (rig.kitBox) disposeObject(rig.kitBox);
      if (rig.kitBoxTwin) disposeObject(rig.kitBoxTwin);
      if (rig.arrow) disposeObject(rig.arrow);
      disposeObject(wall);
      disposeObject(wallLine);
      disposeObject(sideWall);
      disposeObject(sideWallLine);
      disposeObject(grid);
      renderer.dispose();
      renderer.forceContextLoss();
      if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement);
      rigRef.current = null;
    };
  }, []);

  const shown = sizedDef(def, modelBounds, transform);
  const frame = stagedFrame(def, modelBounds, transform);
  const frameKey = JSON.stringify(frame);

  // The procedural prop, its box and the arrow: rebuilt when the prop changes.
  useEffect(() => {
    const rig = rigRef.current;
    if (!rig) return;
    for (const old of [rig.procedural, rig.kitBox, rig.clearZone, rig.holdMarker, rig.kitBoxTwin, rig.arrow, rig.accessory, rig.accessoryTwin]) {
      if (old) { old.removeFromParent(); disposeObject(old); }
    }
    rig.accessory = null;
    rig.accessoryTwin = null;
    rig.clearZone = makeClearZone(def, frame);
    if (rig.clearZone) rig.scene.add(rig.clearZone);
    // The Trusscore accessory the prop is previewed on (a hook, a shelf, a
    // basket): its real STL, flush on the wall as the designer mounts it, with
    // the point it holds at exactly on the gold ball — one at the origin for
    // the stand-in, a twin beside the model in side-by-side mode.
    const kind = frame.kind === 'held' && def.kind === 'slatwall' ? def.hangsOn?.[0] : undefined;
    if (kind) {
      const token = rig.loadToken;
      void loadAccessoryGeometry(kind).then((shared) => {
        if (rigRef.current !== rig || rig.loadToken !== token || rig.accessory) return;
        const acc = accessoryDef(kind);
        const make = () => {
          const geometry = shared.clone();
          // Its bottom-left-back corner sits so that holdAt lands on the origin; the tabs go into the slats behind the wall plane.
          geometry.translate(-acc.holdAt.x, -acc.holdAt.y, frame.wallZ - acc.seat.tabDepth);
          const mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: 0x1b1b1e, roughness: 0.55, metalness: 0.35 }));
          const g = new THREE.Group();
          g.add(mesh);
          return g;
        };
        rig.accessory = make();
        rig.accessoryTwin = make();
        rig.scene.add(rig.accessory);
        rig.candidateRoot.add(rig.accessoryTwin);
        placeRef.current();
      }).catch(() => undefined);
    }
    if (frame.kind === 'floor') {
      // The stand-in at the size the room stages the model at (variants.ts stagedSizeFeet), as the designer draws it --
      // not the kit's box, which for a prop made here is a placeholder (1 x 1 x 1) the model's own size replaces.
      rig.procedural = buildFloorProp(shown);
      // Where the room draws it: at the staged body's centre (the model's box, which Prop Studio's offset
      // may have moved off the prop's origin -- an island out in the room), as stagingMeshes does with bodyOffset.
      rig.procedural.position.set((frame.box.min[0] + frame.box.max[0]) / 2, 0, (frame.box.min[2] + frame.box.max[2]) / 2);
      rig.holdMarker = null;
    } else {
      // Held at (0, 0) in wall coordinates, drawn holdZ out from the wall:
      // moved back by holdZ so the hold point — the origin of the frame — is
      // where the builder put the prop's hold, and the wall is at −holdZ.
      // … as the model's box when there is one (sizedDef / heldShift): the stand-in follows the model.
      const [ox, oz] = heldShift(def, modelBounds, transform);
      rig.procedural = buildHungProp(shown, 0, 0, frame.holdZ);
      rig.procedural.position.set(ox, 0, -frame.holdZ + oz);
      rig.holdMarker = makeHoldMarker();
      rig.scene.add(rig.holdMarker);
    }
    rig.kitBox = makeKitBox(frame.box, 1);
    rig.kitBoxTwin = makeKitBox(frame.box, 0.45);
    rig.arrow = makeRoomArrow(frame.box);
    rig.scene.add(rig.procedural, rig.kitBox, rig.kitBoxTwin, rig.arrow);
  }, [def, frameKey]); // frameKey: the frame is derived from def; listed so a change to how it is derived rebuilds too

  // A fresh view when the prop or the arrangement changes — ANOTHER prop (by
  // id), the other mode, a candidate arriving or going — never when a number
  // of the same prop changes (its size, hold point, clearance: each saved
  // change is a new definition object): the user's own orbit stays, so the
  // effect of the change can be seen from where they were looking.
  const hasCandidate = !!candidateUrl;
  const defRef = useRef(def);
  defRef.current = def;
  useEffect(() => {
    const rig = rigRef.current;
    const { modelBounds: mb, transform: t } = latestRef.current;
    if (rig) fitCamera(rig, stagedFrame(defRef.current, mb, t), mode, hasCandidate);
  }, [def.id, mode, hasCandidate]);

  /**
   * Placement and arrangement, from the CURRENT props: the candidate put where
   * placement.ts says, the prop ghosted or not, the twin box and the wall
   * stretched to the scene. Cheap, so it runs on every change — and from the
   * loader the moment a candidate arrives, so a freshly loaded model is never
   * drawn raw (metres as feet, unturned, off its floor-centre) while the
   * readouts beside it show the placed numbers.
   */
  const place = () => {
    const rig = rigRef.current;
    if (!rig) return;
    const two = mode === 'side' && hasCandidate;
    const sideX = mode === 'side' ? frame.box.max[0] - frame.box.min[0] + GAP_FT : 0;
    rig.candidateRoot.position.set(sideX, 0, 0);
    let top = frame.box.max[1];
    let bottom = frame.box.min[1];
    if (rig.candidate) {
      if (modelBounds) {
        const p = placeInThree({ modelBounds, transform });
        rig.candidate.position.set(p.position[0], p.position[1], p.position[2]);
        rig.candidate.quaternion.set(p.quaternion[0], p.quaternion[1], p.quaternion[2], p.quaternion[3]);
        rig.candidate.scale.set(p.scale[0], p.scale[1], p.scale[2]);
        rig.candidate.visible = true;
        // The copies: as many clones as the transform asks for, each the
        // placed model moved by its copy's offset. A clone shares the loaded
        // geometry and materials, so it is removed, never disposed.
        const moves = copyOffsetsFeet(modelBounds, transform);
        while (rig.copies.length < moves.length - 1) {
          const clone = rig.candidate.clone(true);
          rig.candidateRoot.add(clone);
          rig.copies.push(clone);
        }
        while (rig.copies.length > moves.length - 1) {
          const extra = rig.copies.pop();
          if (extra) rig.candidateRoot.remove(extra);
        }
        rig.copies.forEach((clone, i) => {
          const move = moves[i + 1];
          clone.position.set(p.position[0] + move[0], p.position[1] + move[1], p.position[2] + move[2]);
          clone.quaternion.set(p.quaternion[0], p.quaternion[1], p.quaternion[2], p.quaternion[3]);
          clone.scale.set(p.scale[0], p.scale[1], p.scale[2]);
          clone.visible = true;
        });
        const placed = placedBoundsFeet(modelBounds, transform);
        top = Math.max(top, placed.max[1]);
        bottom = Math.min(bottom, placed.min[1]);
        // The placed box, in white, round the model: THIS is the room staging
        // keeps for it (its reach and drop for a held prop), not the kit box.
        if (rig.placedBox) { rig.candidateRoot.remove(rig.placedBox); disposeObject(rig.placedBox); }
        rig.placedBox = makeKitBox(placed, 0.85, 0xf2f2f2);
        rig.candidateRoot.add(rig.placedBox);
      } else {
        rig.candidate.visible = false; // no bounds recorded: nothing to place it by
        for (const clone of rig.copies) clone.visible = false;
        if (rig.placedBox) rig.placedBox.visible = false;
      }
    } else if (rig.placedBox) {
      rig.placedBox.visible = false;
    }
    // The hang point lands at the offset (the placement puts it at the
    // origin, then moves the model by the offset): the marker is there, on
    // the model, and over the gold ball when the offset is zero.
    if (rig.hangMarker.parent !== rig.candidateRoot) rig.candidateRoot.add(rig.hangMarker);
    rig.hangMarker.position.set(transform.offset[0], transform.offset[1], transform.offset[2]);
    rig.hangMarker.visible = showMarkers && showHang && !!rig.candidate?.visible;
    // The hook beside the model, side by side (overlaid, the prop's own gold
    // ball is in the same place): an offset reads as the ring's distance from it.
    if (rig.holdTwin.parent !== rig.candidateRoot) rig.candidateRoot.add(rig.holdTwin);
    rig.holdTwin.visible = showMarkers && frame.kind === 'held' && mode === 'side' && !!rig.candidate?.visible;
    if (rig.holdMarker) rig.holdMarker.visible = showMarkers;
    // The accessory's twin beside the model, side by side only (overlaid, the one at the origin is it).
    if (rig.accessoryTwin) rig.accessoryTwin.visible = mode === 'side' && !!rig.candidate?.visible;
    // The parts: each placed in the candidate's frame by its own transform,
    // with its own copies; the one being placed gets a box round it.
    if (rig.partBox) { rig.candidateRoot.remove(rig.partBox); disposeObject(rig.partBox); rig.partBox = null; }
    for (const part of parts) {
      const slot = rig.parts.get(part.key);
      if (!slot?.root) continue;
      if (!part.modelBounds) { slot.root.visible = false; continue; }
      const p = placeInThree({ modelBounds: part.modelBounds, transform: part.transform });
      const moves = copyOffsetsFeet(part.modelBounds, part.transform);
      slot.root.position.set(p.position[0], p.position[1], p.position[2]);
      slot.root.quaternion.set(p.quaternion[0], p.quaternion[1], p.quaternion[2], p.quaternion[3]);
      slot.root.scale.set(p.scale[0], p.scale[1], p.scale[2]);
      slot.root.visible = true;
      while (slot.copies.length < moves.length - 1) { const c = slot.root.clone(true); rig.candidateRoot.add(c); slot.copies.push(c); }
      while (slot.copies.length > moves.length - 1) { const c = slot.copies.pop(); if (c) rig.candidateRoot.remove(c); }
      slot.copies.forEach((c, i) => {
        const m = moves[i + 1];
        c.position.set(p.position[0] + m[0], p.position[1] + m[1], p.position[2] + m[2]);
        c.quaternion.set(p.quaternion[0], p.quaternion[1], p.quaternion[2], p.quaternion[3]);
        c.scale.set(p.scale[0], p.scale[1], p.scale[2]);
        c.visible = true;
      });
      const placed = placedBoundsFeet(part.modelBounds, part.transform);
      top = Math.max(top, placed.max[1]);
      bottom = Math.min(bottom, placed.min[1]);
      if (part.selected) {
        rig.partBox = makeKitBox(placed, 0.9);
        rig.partBox.renderOrder = 10;
        rig.candidateRoot.add(rig.partBox);
      }
    }
    const overlay = mode === 'overlay';
    rig.procedural?.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const m = mesh.material as THREE.Material;
      m.transparent = overlay;
      m.opacity = overlay ? GHOST_OPACITY : 1;
      m.depthWrite = !overlay;
      m.needsUpdate = true;
    });
    if (rig.kitBox) {
      // Over the candidate in overlay mode: drawn last, through everything.
      (rig.kitBox.material as THREE.Material).depthTest = !overlay;
      rig.kitBox.renderOrder = overlay ? 10 : 0;
    }
    if (rig.kitBoxTwin) {
      rig.kitBoxTwin.visible = mode === 'side' && !!rig.candidate;
      rig.kitBoxTwin.position.x = sideX;
    }
    // The wall: where the frame puts it (z = −d/2 for a floor prop, −holdZ
    // for a held one) across the prop and the candidate beside it, a little
    // past them either side, from a foot under the lowest of the kit box and
    // the placed model to a foot over the highest.
    const span = (two ? 2 * def.size.w + GAP_FT : def.size.w) + 2 * WALL_MARGIN_FT;
    const cx = two ? (def.size.w + GAP_FT) / 2 : 0;
    // The floor: under a floor prop's feet; for a wall prop, its "Held at"
    // height below the hold point, so the number shows as the bar's height
    // up the wall. (A hung prop's height is its accessory's: the floor stays at the hold.)
    const floorY = def.kind === 'wall' ? -(def.mountHeight ?? 4) : 0;
    rig.grid.position.y = floorY;
    if (rig.clearZone && frame.kind === 'floor') rig.clearZone.position.y = 0;
    const low = Math.min(floorY, bottom) - (frame.kind === 'held' && def.kind !== 'wall' ? 1 : 0);
    const height = top + 1 - low;
    const z = frame.wallZ;
    rig.wall.position.set(cx, low + height / 2, z);
    rig.wall.scale.set(span, height, 1);
    rig.wallLine.position.set(cx, low + 0.025, z);
    rig.wallLine.scale.set(span, 1, 1);
    // A corner prop's second wall: tight to the kit box's LEFT side (where
    // staging puts the corner), from the wall behind to past the box's front
    // and what it keeps clear there.
    const corner = def.anchor === 'corner' && frame.kind === 'floor';
    rig.sideWall.visible = rig.sideWallLine.visible = corner;
    if (corner) {
      const depth = def.size.d / 2 + (def.clearance ?? 0) + WALL_MARGIN_FT - z;
      const x = -def.size.w / 2;
      rig.sideWall.position.set(x, low + height / 2, z + depth / 2);
      rig.sideWall.scale.set(depth, height, 1);
      rig.sideWallLine.position.set(x, low + 0.025, z + depth / 2);
      rig.sideWallLine.scale.set(depth, 1, 1);
    }
    rig.render();
  };
  const placeRef = useRef(place);
  placeRef.current = place;

  // The candidate: loaded when its file changes.
  useEffect(() => {
    const rig = rigRef.current;
    if (!rig) return;
    if (rig.candidate) {
      // The clones first — they share what disposeObject frees.
      for (const clone of rig.copies) rig.candidateRoot.remove(clone);
      rig.copies = [];
      rig.candidateRoot.remove(rig.candidate);
      disposeObject(rig.candidate);
      rig.candidate = null;
    }
    const token = ++rig.loadToken;
    if (!candidateUrl) {
      onCandidateRef.current?.({ phase: 'none' });
      rig.render();
      return;
    }
    onCandidateRef.current?.({ phase: 'loading' });
    new GLTFLoader().loadAsync(candidateUrl).then((gltf) => {
      if (rigRef.current !== rig || token !== rig.loadToken) { disposeObject(gltf.scene); return; }
      // The file's own size (glTF axes) in Blender order — along, deep, tall —
      // so the panel can hold it against the bounds the manifest recorded.
      // Measured over the vertices (`precise`): a mesh's cached box put
      // through its rotation overstates the size (the bicycle's tyres add
      // 30 cm), and the manifest's bounds were measured over vertices.
      // Measured BEFORE it is placed: placed, the box would be in feet.
      const size = new THREE.Box3().setFromObject(gltf.scene, true).getSize(new THREE.Vector3());
      rig.candidate = gltf.scene;
      rig.candidateRoot.add(gltf.scene);
      // Placed before anyone sees it, at the transform the panel shows now.
      placeRef.current();
      onCandidateRef.current?.({ phase: 'ready', sizeMetres: [size.x, size.z, size.y] });
    }).catch((err: unknown) => {
      if (rigRef.current !== rig || token !== rig.loadToken) return;
      const message = err instanceof Error ? err.message : String(err);
      onCandidateRef.current?.({ phase: 'error', message: S.viewer.loadFailed(message) });
    });
  }, [candidateUrl]);

  // The parts' previews: loaded as they come and go (keyed by their files),
  // placed by place() like the candidate.
  const partFiles = JSON.stringify(parts.map((p) => [p.key, p.url]));
  useEffect(() => {
    const rig = rigRef.current;
    if (!rig) return;
    const wanted = new Map(parts.map((p) => [p.key, p.url]));
    for (const [key, slot] of [...rig.parts]) {
      if (wanted.get(key) === slot.url) continue;
      for (const c of slot.copies) rig.candidateRoot.remove(c);
      if (slot.root) { rig.candidateRoot.remove(slot.root); disposeObject(slot.root); }
      rig.parts.delete(key);
    }
    for (const [key, url] of wanted) {
      if (rig.parts.has(key)) continue;
      // Their own counter: bumping the candidate's would discard its load in flight.
      const slot = { url, root: null as THREE.Object3D | null, copies: [] as THREE.Object3D[], token: ++rig.partLoads };
      rig.parts.set(key, slot);
      if (!url) continue;
      const token = slot.token;
      new GLTFLoader().loadAsync(url).then((gltf) => {
        if (rigRef.current !== rig || rig.parts.get(key) !== slot || slot.token !== token) { disposeObject(gltf.scene); return; }
        slot.root = gltf.scene;
        rig.candidateRoot.add(gltf.scene);
        placeRef.current();
      }).catch(() => { /* a part that will not load is simply not drawn */ });
    }
    placeRef.current();
  }, [partFiles]); // the files alone: `parts` itself changes every render

  // Every change to what is placed or how it is arranged.
  const partPlacements = JSON.stringify(parts.map((p) => [p.key, p.transform, p.selected, p.modelBounds]));
  useEffect(() => {
    placeRef.current();
  }, [def, modelBounds, transform, mode, candidateUrl, partPlacements, showHang, showMarkers]);

  // Picking the hang point: a click on the model — not the end of an orbit
  // drag — is turned into a point in the model's own frame (the glTF scene's
  // local space, put back into Blender axes: what the bounds are in).
  const pickRef = useRef({ picking, onPick, onCancelPick });
  pickRef.current = { picking, onPick, onCancelPick };
  const downAt = useRef<{ x: number; y: number } | null>(null);
  const pickAt = (clientX: number, clientY: number) => {
    const rig = rigRef.current;
    const model = rig?.candidate;
    if (!rig || !model || !model.visible) return;
    const rect = rig.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc, rig.camera);
    rig.scene.updateMatrixWorld(true);
    const hit = ray.intersectObject(model, true)[0];
    if (!hit) return;
    const local = model.worldToLocal(hit.point.clone());
    const hang = gltfToBlender([local.x, local.y, local.z]).map((v) => Math.round(v * 100000) / 100000) as Vec3;
    pickRef.current.onPick?.(hang);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && pickRef.current.picking) {
      e.preventDefault();
      pickRef.current.onCancelPick?.();
      return;
    }
    const step = e.shiftKey ? 1 : 0.1;
    let dx = 0, dz = 0;
    if (e.key === 'ArrowLeft') dx = -step;
    else if (e.key === 'ArrowRight') dx = step;
    else if (e.key === 'ArrowUp') dz = -step; // away from the viewer: toward the wall
    else if (e.key === 'ArrowDown') dz = step;
    else return;
    e.preventDefault();
    onNudgeRef.current?.(dx, dz);
  };

  return (
    <div
      ref={containerRef}
      className="prop-studio-viewer"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onPointerDown={(e) => { e.currentTarget.focus({ preventScroll: true }); downAt.current = { x: e.clientX, y: e.clientY }; }}
      onPointerUp={(e) => {
        const d = downAt.current;
        downAt.current = null;
        // A click, not an orbit: the pointer barely moved.
        if (picking && d && Math.hypot(e.clientX - d.x, e.clientY - d.y) < 5) pickAt(e.clientX, e.clientY);
      }}
      style={{ position: 'absolute', inset: 0, outline: 'none', overflow: 'hidden', cursor: picking ? 'crosshair' : undefined }}
    />
  );
});

export default PropViewer;
